import axios from "axios";
import type {
  Branding,
  CatalogCollection,
  ClientConfig,
  DetectedObject,
  ExtractionStatus,
  IntegrateRequest,
  IntegrateResult,
  LeadSubmission,
  PairingSlot,
  PostcardLandingPayload,
  PostcardQuoteSubmission,
  RenderRequest,
  RenderResult,
  ResolveAddressPhotoResult,
  ResolveResult,
  SampleHome,
  SellPriceQuote,
  TopProfile,
  UploadTicket,
} from "./types";
import { sizeDims, type DoorSize } from "../geometry/doorSize";

const http = axios.create({ baseURL: "/api/v1" });

// The scope token may be composite ("manufacturer/amarr") — encode per segment so the path
// separator survives and the request hits the manufacturer-scoped API route.
const base = (slug: string) =>
  `/visualizer/${slug.split("/").map(encodeURIComponent).join("/")}`;

// Attribute writes to the analytics session the host created at extraction time.
const sessionHeader = (sessionId?: string | null) =>
  sessionId ? { headers: { "X-Visualizer-Session": sessionId } } : undefined;

/**
 * Canonicalize a detection response to lowercase enum values so app logic never depends on the host's enum
 * casing. This host emits camelCase already (so it's a no-op today), but it keeps detection processing
 * identical to the proposal SPA — which normalizes the platform host's PascalCase at this same boundary —
 * and guards against a future serializer change silently dropping an Angled/Arched opening to flat.
 */
function normalizeExtraction(s: ExtractionStatus): ExtractionStatus {
  return {
    ...s,
    objects: (s.objects ?? []).map((o) => ({
      ...o,
      doorType: o.doorType ? (o.doorType.toLowerCase() as DetectedObject["doorType"]) : o.doorType,
      topProfile: o.topProfile
        ? { ...o.topProfile, kind: o.topProfile.kind.toLowerCase() as TopProfile["kind"] }
        : o.topProfile,
    })),
  };
}

// Route-forced catalogue mode: "full" = showroom (price-free full active catalogue), "own" =
// explicitly priced. Absent → no param → the SERVER applies the tenant's configured default, so a
// bare link can open in either mode.
const catalogueParams = (opts?: { mode?: "full" | "own" | null }) =>
  opts?.mode ? { params: { catalogue: opts.mode } } : undefined;

export interface ResolveParams {
  collectionId: string;
  designId?: string;
  colorId?: string;
  windowOptionId?: string;
  glassTypeId?: string;
  hardwareId?: string;
  colorGroup?: string;
  width?: number;
  height?: number;
}

// The v2 price body — the server's `PriceRequestV2` exactly (Contracts/DoorPricing/DealerPricing.cs).
// Every field is required on the wire; the optional ones are sent as null rather than omitted, so the
// body reads the same whether or not the homeowner has made a pick.
export interface PriceRequest {
  collectionId: string;
  // Optional on the wire: the edge resolves explicit → the collection's declared default → its only
  // construction. Non-visual; its upcharge is inside the answer's figure, not beside it.
  constructionId: string | null;
  designId: string | null;
  colorId: string | null;
  overlayColorId: string | null;
  windowOptionId: string | null;
  glassTypeId: string | null;
  windowCount: number | null;
  hardwareId: string | null;
  closedStyle: string | null;
  widthIn: number;
  heightIn: number;
  sectionCount: number | null;
}

/**
 * The ONE place a visualizer selection becomes a price body — the client mirror of the server's
 * `VisualizerPriceSelection.EdgeRequestFor`, and the only site that converts the door's size.
 *
 * **The ×12 is the whole point.** `sizeDims` returns FEET (`{width: 16, height: 7}`) and
 * `PriceRequestV2` takes INCHES, so a copy of the number would ask for a 16-inch door. Multiplied,
 * the two door classes land on exactly 96×84 and 192×84 — which is `PriceAnchorSize`, the tuple every
 * migrated pin was written at. That is not a coincidence to preserve loosely: `sizeDims` is a CLASS
 * ANCHOR (single = 8×7, double = 16×7), never a measured opening, and a pins-only tenant has no
 * laddering, so any other width answers "quote required".
 *
 * **Three axes stay null on purpose.** `windowCount` — the book's `QuantityRule` is authoritative and
 * the edge must not invent a count; `sectionCount` — no section picker exists (plan appendix B3);
 * `closedStyle` — the server DERIVES it from the chosen window option, and the SPA offers no picker to
 * override it with. `overlayColorId` is null for the same reason: no two-tone overlay picker on this
 * surface. `glassTypeId` IS offered here, so it is forwarded — it reaches `Windows.GlassCode` and the
 * `ConfigKey`, and a closed door drops it server-side rather than refusing.
 *
 * **What this body cannot carry, stated because it is a real (bounded) loss.** The legacy GET took
 * `colorGroup`/`windowGroup` — caller HINTS into legacy group matching, not axes of the door — and
 * `PriceRequestV2` has no slot for them. An UN-FLIPPED tenant answering this POST is served by the
 * legacy arm WITHOUT those hints, so a multi-style collection whose rules are keyed by colour family
 * or window placement can resolve a different style (or none) than the GET did. Flipped tenants are
 * unaffected — the new world matches on codes, not groups.
 */
export function priceRequestFor(
  collectionId: string,
  selection: {
    designId?: string | null;
    colorId?: string | null;
    windowOptionId?: string | null;
    glassTypeId?: string | null;
    hardwareId?: string | null;
    constructionId?: string | null;
  } | null | undefined,
  size: DoorSize,
): PriceRequest {
  const dims = sizeDims(size);
  return {
    collectionId,
    constructionId: selection?.constructionId ?? null,
    designId: selection?.designId ?? null,
    colorId: selection?.colorId ?? null,
    overlayColorId: null,
    windowOptionId: selection?.windowOptionId ?? null,
    glassTypeId: selection?.glassTypeId ?? null,
    windowCount: null,
    hardwareId: selection?.hardwareId ?? null,
    closedStyle: null,
    widthIn: dims.width * 12,
    heightIn: dims.height * 12,
    sectionCount: null,
  };
}

export const api = {
  getBranding: (slug: string, opts?: { mode?: "full" | "own" | null }) =>
    http.get<Branding>(`${base(slug)}/branding`, catalogueParams(opts)).then((r) => r.data),

  getCollections: (slug: string, opts?: { mode?: "full" | "own" | null }) =>
    http.get<CatalogCollection[]>(`${base(slug)}/catalog/collections`, catalogueParams(opts)).then((r) => r.data),

  // Sample home photos for the "no photo handy? try a sample" row (global starters + the dealer's own).
  getSamples: (slug: string) =>
    http.get<SampleHome[]>(`${base(slug)}/samples`).then((r) => r.data),

  resolve: (slug: string, params: ResolveParams) =>
    http.get<ResolveResult>(`${base(slug)}/catalog/resolve`, { params }).then((r) => r.data),

  // Prices the current selection. `quote: null` is the ENVELOPE's own row — an unpriced scope
  // (showroom/manufacturer) or a collection the dealer switched off — and is distinct from a quote
  // that arrives with a non-"priced" status. Both read as "Pricing on request"; only the second one
  // carries flags anyone can act on.
  price: (slug: string, request: PriceRequest) =>
    http
      .post<{ quote: SellPriceQuote | null }>(`${base(slug)}/catalog/price`, request)
      .then((r) => r.data.quote),

  createPairing: (slug: string) =>
    http.post<PairingSlot>(`${base(slug)}/pair`).then((r) => r.data),

  getPairing: (slug: string, pairingId: string) =>
    http.get<PairingSlot>(`${base(slug)}/pair/${pairingId}`).then((r) => r.data),

  completePairing: (slug: string, pairingId: string, photoUrl: string) =>
    http.post(`${base(slug)}/pair/${pairingId}/complete`, { photoUrl }),

  createUploadTicket: (slug: string, file: File) =>
    http
      .post<UploadTicket>(`${base(slug)}/uploads/ticket`, {
        fileName: file.name,
        contentType: file.type || "image/jpeg",
        sizeBytes: file.size,
      })
      .then((r) => r.data),

  uploadToTicket: (ticket: UploadTicket, file: File) =>
    axios.put(ticket.uploadUrl, file, { headers: { "Content-Type": ticket.contentType } }),

  startExtract: (slug: string, photoUrl: string) =>
    http
      .post<{ jobId: string; sessionId: string | null }>(`${base(slug)}/extract-geometry`, { photoUrl })
      .then((r) => r.data),

  // Resolves a home photo for a street address (DoorLeadScore Street View pipeline) to feed the upload flow.
  resolveAddressPhoto: (slug: string, address: string) =>
    http
      .post<ResolveAddressPhotoResult>(`${base(slug)}/resolve-address-photo`, { address })
      .then((r) => r.data),

  getExtract: (slug: string, jobId: string) =>
    http.get<ExtractionStatus>(`${base(slug)}/extract-geometry/${jobId}`).then((r) => normalizeExtraction(r.data)),

  render: (slug: string, req: RenderRequest, sessionId?: string | null, mode?: "full" | "own" | null) =>
    http.post<RenderResult>(`${base(slug)}/render`, req, { ...sessionHeader(sessionId), ...catalogueParams({ mode }) }).then((r) => r.data),

  integrate: (slug: string, req: IntegrateRequest, sessionId?: string | null, mode?: "full" | "own" | null) =>
    http.post<IntegrateResult>(`${base(slug)}/integrate`, req, { ...sessionHeader(sessionId), ...catalogueParams({ mode }) }).then((r) => r.data),

  submitLead: (slug: string, lead: LeadSubmission, sessionId?: string | null) =>
    http.post(`${base(slug)}/leads`, lead, sessionHeader(sessionId)),

  // Postcard QR landing: the code personalizes the session (photo + detections + door presets).
  // 404 = the branded "link expired" card, not an error state.
  getPostcardLanding: (slug: string, code: string) =>
    http
      .get<PostcardLandingPayload>(`${base(slug)}/postcards/${encodeURIComponent(code)}`)
      .then((r) => r.data),

  // Quote from a postcard session — written onto the dealer's known lead (identity = the code).
  submitPostcardQuote: (slug: string, code: string, quote: PostcardQuoteSubmission) =>
    http.post(`${base(slug)}/postcards/${encodeURIComponent(code)}/quote`, quote),
};

// App-wide (not tenant-scoped) client config — ships the browser Google Maps key for address autocomplete.
export const configApi = {
  get: () => http.get<ClientConfig>("/config").then((r) => r.data),
};
